import { Component, ElementRef, HostListener, Input, QueryList, ViewChildren } from '@angular/core';
import { Report } from '../../models/report.model';
import { FileMedia } from '../../models/file-media.model';
import { ReportService } from '../../services/report.service';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ReportCancelModalComponent } from '../report-cancel-modal/report-cancel-modal.component';

@Component({
  selector: 'app-report-modal',
  templateUrl: './report-modal.component.html',
  styleUrl: './report-modal.component.scss'
})
export class ReportModalComponent {

  @Input() report?: Report;

  /* Las miniaturas son botones reales para poder devolverles el foco cuando se
     cierra el visor. Se consultan por QueryList en vez de guardar el
     HTMLElement del click: así no retenemos un nodo que Angular podría haber
     destruido si la lista de fotos cambiara. */
  @ViewChildren('reportThumb') reportThumbs?: QueryList<ElementRef<HTMLButtonElement>>;

  /* Estado del visor a pantalla completa (p-galleria). */
  viewerVisible = false;
  viewerIndex = 0;

  /* Miniatura desde la que se abrió el visor, para devolverle el foco al
     cerrar. No es lo mismo que viewerIndex, que va cambiando al navegar. */
  private viewerOpenerIndex = 0;

  constructor(
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private reportService: ReportService,
  ) { }

  /* -------------------------------------------------------------------------- */
  /*                                 ANCHOR fotos                               */
  /* -------------------------------------------------------------------------- */
  // region fotos
  get files(): FileMedia[] {
    return this.report?.files ?? [];
  }

  /* Con una sola foto no hay a dónde navegar: sin flechas, sin contador y sin
     circular. */
  get hasManyFiles(): boolean {
    return this.files.length > 1;
  }

  /* Los puntos indicadores solo ayudan mientras se puedan contar de un vistazo.
     Pasando de 10 se vuelven ruido y el contador "3 / 12" basta. */
  get showViewerIndicators(): boolean {
    return this.files.length > 1 && this.files.length <= 10;
  }

  /* Texto alternativo real para lector de pantalla: el fileId no le dice nada a
     nadie. */
  photoAlt(index: number): string {
    const title = this.report?.title ?? 'sin título';
    return `Foto ${index + 1} de ${this.files.length} del reporte ${title}`;
  }

  /* El template "item" del galleria recibe el archivo, no su índice, así que el
     índice se resuelve contra la misma lista que alimenta al componente. */
  photoAltOf(file: FileMedia): string {
    return this.photoAlt(this.files.indexOf(file));
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR openViewer                             */
  /* -------------------------------------------------------------------------- */
  // region openViewer
  openViewer(index: number) {
    /* El índice se asigna ANTES que visible y en el mismo tick: p-galleria crea
       su contenido con *ngIf="visible" y el hijo p-galleriaContent lee
       activeIndex al construirse. Si se abriera primero y se moviera el índice
       después, el visor arrancaría siempre en la foto 1. */
    this.viewerIndex = index;
    this.viewerOpenerIndex = index;
    this.viewerVisible = true;
  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR closeViewer                             */
  /* -------------------------------------------------------------------------- */
  // region closeViewer
  closeViewer() {
    if (!this.viewerVisible) {
      return;
    }

    this.viewerVisible = false;
    this.restoreThumbFocus();
  }

  /* p-galleria también se cierra solo (botón X o click en la máscara) y avisa
     por visibleChange. */
  onViewerVisibleChange(visible: boolean) {
    this.viewerVisible = visible;

    if (!visible) {
      this.restoreThumbFocus();
    }
  }

  private restoreThumbFocus() {
    const thumb = this.reportThumbs?.get(this.viewerOpenerIndex);

    if (!thumb) {
      return;
    }

    /* El pFocusTrap del galleria sigue vivo durante la animación de cierre
       (150 ms por defecto) y reclamaría el foco. Se devuelve después. */
    setTimeout(
      () => thumb.nativeElement.focus(),
      200
    );
  }

  /* -------------------------------------------------------------------------- */
  /*                           ANCHOR onDocumentKeydown                         */
  /* -------------------------------------------------------------------------- */
  // region onDocumentKeydown
  /* PrimeNG 17 solo atiende flechas cuando el foco está sobre las miniaturas o
     los indicadores del galleria, y no cierra con Esc en modo pantalla
     completa. El teclado del visor se resuelve aquí. */
  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent) {

    if (!this.viewerVisible) {
      return;
    }

    switch (event.key) {

      case 'Escape':
        /* preventDefault evita que el modal de ng-bootstrap se cierre junto con
           el visor: ngb decide dentro de un requestAnimationFrame mirando
           event.defaultPrevented, así que con marcarlo basta. */
        event.preventDefault();
        this.closeViewer();
        break;

      case 'ArrowRight':
        if (!this.hasManyFiles) {
          return;
        }
        event.preventDefault();
        this.viewerIndex = (this.viewerIndex + 1) % this.files.length;
        break;

      case 'ArrowLeft':
        if (!this.hasManyFiles) {
          return;
        }
        event.preventDefault();
        this.viewerIndex = (this.viewerIndex - 1 + this.files.length) % this.files.length;
        break;

    }
  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR cancel                               */
  /* -------------------------------------------------------------------------- */
  // region cancel
  /* La razón es obligatoria, así que esto no cabe en un confirm(): se abre el
     modal de cancelación encima del detalle. Si la cancelación prospera el
     detalle se cierra —el reporte ya no admite ninguna acción y el listado de
     atrás se refresca solo desde el servicio—; si se descarta, el detalle se
     queda abierto tal cual estaba. */
  async cancel() {

    const modalRef = this.modalService.open(
      ReportCancelModalComponent
    );

    modalRef.componentInstance.report = this.report!;

    /* `result` rechaza cuando el modal se descarta (Esc, la X, "Volver"). Ese
       rechazo no es un error: solo significa que no se canceló nada. */
    const cancelReason = await modalRef.result.catch(
      () => undefined
    );

    if(cancelReason) {
      this.activeModal.close();
    }

  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR delete                               */
  /* -------------------------------------------------------------------------- */
  // region delete
  async delete() {
    const flag = confirm(`¿Está seguro que desea borrar el reporte?`);

    if (flag) {
      const result = await this.reportService.delete(
        this.report!
      );

      if(result) {
        this.activeModal.close();
      }

    }
  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR updateStatus                            */
  /* -------------------------------------------------------------------------- */
  // region updateStatus
  async updateStatus() {

    /* El destino se lee del modelo (nextStatusString) y se arma ANTES de
       tocar nada: si el usuario cancela, no se llama a la API ni se mueve el
       estado local. */
    const flag = confirm(`¿Pasar el reporte "${this.report!.title}" a ${this.report!.nextStatusString.toLowerCase()}?`);

    if (flag) {
      const result = await this.reportService.update(
        this.report!
      );

      if(result) {
        this.report!.status = this.report!.nextStatus;
      }

    }
  }
}
